import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, esGestorPersonal } from '../stores/auth';
import {
  alertasList, alertaDespachar, alertaTomar, alertaCerrar,
  type Alerta, ESTADO_META, TIPO_META, ESTADOS_ABIERTOS,
} from '../api/seguridad';
import MapaAlertas from '../components/MapaAlertas';
import { armarAlarma, sonarAlarma } from '../lib/alarma';

const ROL_LABEL: Record<string, string> = {
  superadmin: 'Superadmin', admin: 'Admin municipal', jefe_seguridad: 'Jefe de Seguridad',
  agente_seguridad: 'Agente', radio_operador: 'Radio-Operador',
};

const POLL_MS = 5000;

function haceCuanto(iso: string): string {
  const t = new Date(iso.replace(' ', 'T')).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  return new Date(iso.replace(' ', 'T')).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

function abierta(a: Alerta) { return ESTADOS_ABIERTOS.includes(a.estado); }

/** URL de Google Maps con la ruta hasta el punto de la alerta (desde la ubicación
 *  del agente). En el celular abre la app de Google Maps con la navegación. */
function comoLlegarUrl(a: Alerta): string | null {
  if (a.latitud == null || a.longitud == null) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${a.latitud},${a.longitud}&travelmode=driving`;
}

export default function Home() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const nav = useNavigate();

  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [soloAbiertas, setSoloAbiertas] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [nota, setNota] = useState('');
  const [menuAbierto, setMenuAbierto] = useState(false);

  const knownPending = useRef<Set<number>>(new Set());
  const primeraCarga = useRef(true);

  const cargar = useCallback(async () => {
    try {
      const { items } = await alertasList(1);
      // Ordenar: pendientes y casos SIN responsable primero (necesitan acción),
      // luego en_atención con responsable, luego cerradas. Dentro, más reciente.
      const prio = (a: Alerta) =>
        a.estado === 'pendiente' ? 0
        : (a.estado === 'en_atencion' && !a.responsable_activo) ? 1
        : abierta(a) ? 2 : 3;
      items.sort((a, b) => {
        const pa = prio(a), pb = prio(b);
        if (pa !== pb) return pa - pb;
        return b.creado_en.localeCompare(a.creado_en);
      });
      // Alarma: si aparece una PENDIENTE nueva (y no es la primera carga)
      const pendAhora = items.filter((a) => a.estado === 'pendiente').map((a) => a.id);
      if (!primeraCarga.current) {
        const nueva = pendAhora.some((id) => !knownPending.current.has(id));
        if (nueva) sonarAlarma();
      }
      knownPending.current = new Set(pendAhora);
      primeraCarga.current = false;
      setAlertas(items);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message || 'Error al cargar alertas');
    }
  }, []);

  useEffect(() => {
    armarAlarma();
    cargar();
    const t = setInterval(cargar, POLL_MS);
    return () => clearInterval(t);
  }, [cargar]);

  const sel = alertas.find((a) => a.id === selectedId) ?? null;

  async function despachar(a: Alerta) {
    setBusy(true);
    try { await alertaDespachar(a.id); await cargar(); }
    catch (e: any) { setErr(e?.message || 'No se pudo despachar'); }
    finally { setBusy(false); }
  }
  async function tomar(a: Alerta) {
    setBusy(true);
    try { await alertaTomar(a.id); await cargar(); }
    catch (e: any) { setErr(e?.message || 'No se pudo tomar'); }
    finally { setBusy(false); }
  }
  async function cerrar(a: Alerta, estado: 'atendida' | 'falsa') {
    setBusy(true);
    try {
      await alertaCerrar(a.id, estado, nota.trim() || undefined);
      setCerrando(false); setNota('');
      await cargar();
    } catch (e: any) { setErr(e?.message || 'No se pudo cerrar'); }
    finally { setBusy(false); }
  }

  // Hasta que incorporemos permisos/roles: TODOS menos el jefe (gestor) ven
  // SOLO los casos pendientes, para tomarlos. El gestor ve la consola completa.
  const esGestor = esGestorPersonal(user?.rol);
  const base = esGestor ? alertas : alertas.filter((a) => a.estado === 'pendiente');
  const visibles = esGestor ? (soloAbiertas ? base.filter(abierta) : base) : base;
  const abiertasCount = base.filter(abierta).length;

  if (!user) return null;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🆘</span>
          <div>
            <h1 className="text-slate-900 font-bold leading-tight">Seguridad · SOS</h1>
            <p className="text-slate-500 text-xs leading-tight">
              {user.destacamento_nombre ?? user.municipio_nombre ?? 'Central'}
              {abiertasCount > 0 && <span className="text-brand-600 font-semibold"> · {abiertasCount} activa{abiertasCount !== 1 ? 's' : ''}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-slate-900 text-sm leading-tight">{user.nombre} {user.apellido}</p>
            <p className="text-brand-600 text-xs font-semibold leading-tight">{ROL_LABEL[user.rol] ?? user.rol}</p>
          </div>

          {/* Nav desktop: botones en línea */}
          <div className="hidden sm:flex items-center gap-2">
            {esGestorPersonal(user.rol) && (
              <>
                <button onClick={() => nav('/guardia')} className="text-slate-600 hover:bg-slate-50 text-xs border border-slate-300 rounded-lg px-3 py-2">🛡 Guardia</button>
                <button onClick={() => nav('/turnos')} className="text-slate-600 hover:bg-slate-50 text-xs border border-slate-300 rounded-lg px-3 py-2">📅 Turnos</button>
                <button onClick={() => nav('/personal')} className="text-slate-600 hover:bg-slate-50 text-xs border border-slate-300 rounded-lg px-3 py-2">👥 Personal</button>
                <button onClick={() => nav('/auditoria')} className="text-slate-600 hover:bg-slate-50 text-xs border border-slate-300 rounded-lg px-3 py-2">📋 Auditoría</button>
              </>
            )}
            <button onClick={logout} className="text-slate-600 hover:bg-slate-50 text-xs border border-slate-300 rounded-lg px-3 py-2">Salir</button>
          </div>

          {/* Nav mobile: menú hamburguesa */}
          <div className="relative sm:hidden">
            <button onClick={() => setMenuAbierto((v) => !v)} aria-label="Menú"
              className="text-slate-700 hover:bg-slate-100 text-xl leading-none border border-slate-300 rounded-lg px-3 py-1.5">☰</button>
            {menuAbierto && (
              <>
                <div className="fixed inset-0 z-[1400]" onClick={() => setMenuAbierto(false)} />
                <div className="absolute right-0 top-full mt-1 z-[1401] bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[172px] overflow-hidden">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-slate-900 text-sm font-semibold leading-tight">{user.nombre} {user.apellido}</p>
                    <p className="text-brand-600 text-xs font-semibold leading-tight">{ROL_LABEL[user.rol] ?? user.rol}</p>
                  </div>
                  {esGestorPersonal(user.rol) && (
                    <>
                      <MenuItem onClick={() => { setMenuAbierto(false); nav('/guardia'); }}>🛡 Guardia</MenuItem>
                      <MenuItem onClick={() => { setMenuAbierto(false); nav('/turnos'); }}>📅 Turnos</MenuItem>
                      <MenuItem onClick={() => { setMenuAbierto(false); nav('/personal'); }}>👥 Personal</MenuItem>
                      <MenuItem onClick={() => { setMenuAbierto(false); nav('/auditoria'); }}>📋 Auditoría</MenuItem>
                      <div className="border-t border-slate-100 my-1" />
                    </>
                  )}
                  <MenuItem onClick={() => { setMenuAbierto(false); logout(); }}>🚪 Salir</MenuItem>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {err && <div className="bg-red-50 border-b border-red-200 text-red-700 text-xs px-4 py-1.5 shrink-0">{err}</div>}

      {/* Cuerpo: lista + mapa */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Lista — en móvil comparte el alto con el mapa (min-h-0 para que el scroll interno funcione) */}
        <aside className="flex-1 min-h-0 lg:flex-none lg:w-[380px] flex flex-col border-r border-slate-200 bg-white overflow-hidden">
          {esGestor ? (
            <div className="flex gap-1 p-2 shrink-0 border-b border-slate-200">
              <button onClick={() => setSoloAbiertas(true)}
                className={`flex-1 text-xs font-semibold rounded-lg py-2 ${soloAbiertas ? 'bg-brand-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                Activas ({abiertasCount})
              </button>
              <button onClick={() => setSoloAbiertas(false)}
                className={`flex-1 text-xs font-semibold rounded-lg py-2 ${!soloAbiertas ? 'bg-brand-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                Todas ({alertas.length})
              </button>
            </div>
          ) : (
            <div className="p-2 shrink-0 border-b border-slate-200">
              <p className="text-center text-xs font-semibold text-slate-600 py-1.5">🚨 Casos pendientes ({base.length})</p>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {visibles.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-10 px-4">
                {esGestor ? (soloAbiertas ? 'Sin alertas activas. Todo tranquilo. 👌' : 'Sin alertas.') : 'Sin casos pendientes. Todo tranquilo. 👌'}
              </p>
            )}
            {visibles.map((a) => {
              const em = ESTADO_META[a.estado] ?? ESTADO_META.pendiente;
              const tm = TIPO_META[a.tipo] ?? { label: a.tipo, icon: '⚠️' };
              const activa = a.id === selectedId;
              return (
                <button key={a.id} onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex gap-3 items-start ${activa ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  style={activa ? { boxShadow: `inset 3px 0 0 ${em.color}` } : {}}>
                  <span className="text-lg leading-none mt-0.5">{tm.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-900 text-sm font-semibold truncate">{a.ciudadano_nombre ?? a.telefono_anonimo ?? 'Anónimo'}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: em.color + '22', color: em.color }}>{em.label}</span>
                    </div>
                    <p className="text-slate-500 text-xs truncate">{a.zona_nombre ?? a.direccion_aprox ?? tm.label}</p>
                    {a.estado === 'en_atencion' && !a.responsable_activo && (
                      <p className="text-red-600 text-[11px] font-semibold">⚠️ Sin responsable — reasignar</p>
                    )}
                    <p className="text-slate-400 text-[11px]">{haceCuanto(a.creado_en)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Mapa + panel de detalle */}
        <div className="relative flex-1 min-h-0">
          <MapaAlertas alertas={visibles} selectedId={selectedId} onSelect={setSelectedId} />

          {sel && (
            <div
              className="fixed lg:absolute inset-x-0 bottom-0 z-[1200] bg-white border-t border-slate-200 p-4 shadow-2xl max-h-[80vh] overflow-y-auto"
              style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span>{(TIPO_META[sel.tipo] ?? { icon: '⚠️' }).icon}</span>
                    <span className="text-slate-900 font-bold">{(TIPO_META[sel.tipo] ?? { label: sel.tipo }).label}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: (ESTADO_META[sel.estado] ?? ESTADO_META.pendiente).color + '22', color: (ESTADO_META[sel.estado] ?? ESTADO_META.pendiente).color }}>
                      {(ESTADO_META[sel.estado] ?? ESTADO_META.pendiente).label}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm mt-1">
                    {sel.ciudadano_nombre ?? sel.telefono_anonimo ?? 'Anónimo'} · {sel.zona_nombre ?? sel.direccion_aprox ?? 's/zona'}
                  </p>
                  <p className="text-slate-400 text-xs">{haceCuanto(sel.creado_en)} · {sel.creado_en}</p>
                  {sel.despachada_en && !sel.atendido_por_nombre && <p className="text-sky-600 text-xs mt-0.5">📡 Despachada al personal de guardia</p>}
                  {sel.atendido_por_nombre && sel.estado === 'en_atencion' && !sel.responsable_activo ? (
                    <p className="text-red-600 text-xs mt-0.5 font-semibold">⚠️ Sin responsable — {sel.atendido_por_nombre} terminó su turno. Reasignar.</p>
                  ) : sel.atendido_por_nombre && (
                    <p className="text-amber-600 text-xs mt-0.5">Tomada por {sel.atendido_por_nombre}</p>
                  )}
                  {sel.descripcion && <p className="text-slate-600 text-sm mt-1 italic">"{sel.descripcion}"</p>}
                </div>
                <button onClick={() => { setSelectedId(null); setCerrando(false); }} className="text-slate-400 hover:text-slate-700 text-lg leading-none">✕</button>
              </div>

              {/* Acciones */}
              {abierta(sel) && !cerrando && (
                <>
                  <div className="flex gap-2 mt-2">
                    {sel.estado === 'pendiente' && !sel.despachada_en && (
                      <button disabled={busy} onClick={() => despachar(sel)} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                        📡 Despachar
                      </button>
                    )}
                    {sel.estado === 'pendiente' && (
                      <button disabled={busy} onClick={() => tomar(sel)} className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                        🚔 Tomar
                      </button>
                    )}
                    {sel.estado === 'en_atencion' && !sel.responsable_activo && (
                      <button disabled={busy} onClick={() => tomar(sel)} className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                        🔄 Reasignar (tomar)
                      </button>
                    )}
                    <button disabled={busy} onClick={() => setCerrando(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">
                      Cerrar
                    </button>
                  </div>
                  {/* "Cómo llegar" solo cuando la alerta fue TOMADA (en_atencion):
                      no tiene sentido ofrecer la ruta antes de que alguien la tome. */}
                  {sel.estado === 'en_atencion' && comoLlegarUrl(sel) && (
                    <a href={comoLlegarUrl(sel)!} target="_blank" rel="noopener noreferrer"
                      className="mt-2 flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl py-2.5 text-sm font-semibold">
                      🧭 Cómo llegar (Google Maps)
                    </a>
                  )}
                </>
              )}
              {abierta(sel) && cerrando && (
                <div className="mt-2 space-y-2">
                  <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Nota de cierre (opcional)"
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500" />
                  <div className="flex gap-2">
                    <button disabled={busy} onClick={() => cerrar(sel, 'atendida')} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold">✅ Atendida</button>
                    <button disabled={busy} onClick={() => cerrar(sel, 'falsa')} className="flex-1 bg-slate-200 hover:bg-slate-300 disabled:opacity-50 text-slate-700 rounded-xl py-2.5 text-sm font-semibold">⚠️ Falsa alarma</button>
                    <button disabled={busy} onClick={() => { setCerrando(false); setNota(''); }} className="text-slate-500 hover:text-slate-800 text-sm px-3">Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Ítem del menú hamburguesa (mobile). */
function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
      {children}
    </button>
  );
}
